import { PagingParameters } from "@/api/dtos";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import {
  useDeleteComplexLink,
  useGetComplexRelationFields,
  useGetNotRelatedResource,
} from "@/api/related-fields";
import { publicAuctionColumnsVisibility } from "@/config/dashboard/public-auction";
import { FormLayoutElement } from "@/config/forms/general";
import { tiragesColumnsVisibility } from "@/config/dashboard/tirages";
import { Auction, Tirage, TirageInAuction } from "@/lib/types";
import { ColumnDef, Row, VisibilityState } from "@tanstack/react-table";
import { Dispatch, ReactNode, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LinkDrawerRowActions } from "@/components/table/LinkDrawerRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";
import { AuctionLinkGridCard, TirageLinkGridCard } from "./relatedLinkGridCards";
import { GridCardWithActions } from "./GridCardWithActions";
import TirageInAuctionDrawer from "./drawer/TirageInAuctionDrawer";

interface ComplexLinkProps {
  id: string;
  tirage: Tirage;
  fields: FormLayoutElement[] | undefined;
  type: LinkableType;
  auction: Auction;
}

const columnsAuction = [
  {
    accessorKey: "auction.ventesDescriptionNumeroVente",
    header: "ventesDescriptionNumeroVente",
    cell: ({ row }: { row: Row<TirageInAuction> }) => {
      return (
        <TextCell value={row.original.auction.ventesDescriptionNumeroVente} />
      );
    },
  },
  {
    accessorKey: "vignette",
    header: "",
    size: 100,
    cell: ({ row }: { row: Row<TirageInAuction> }) => {
      return (
        <ImageCell
          value={""}
          attachments={
            row.original.auction?.attachments ??
            row.original.attachments ??
            []
          }
          domain={"auction"}
        />
      );
    },
  },
  {
    accessorKey: "auction.ventesDescriptionCommissaires",
    header: "ventesDescriptionCommissaires",
    cell: ({ row }: { row: Row<TirageInAuction> }) => {
      return (
        <TextCell value={row.original.auction.ventesDescriptionCommissaires} />
      );
    },
  },
  {
    accessorKey: "auction.ventesDescriptionDate",
    header: "ventesDescriptionDate",
    cell: ({ row }: { row: Row<TirageInAuction> }) => {
      return <TextCell value={row.original.auction.ventesDescriptionDate} />;
    },
  },
  {
    accessorKey: "auction.ventesDescriptionVille",
    header: "ventesDescriptionVille",
    cell: ({ row }: { row: Row<TirageInAuction> }) => {
      return <TextCell value={row.original.auction.ventesDescriptionVille} />;
    },
  },
  {
    accessorKey: "auction.ventesDescriptionPays",
    header: "ventesDescriptionPays",
    cell: ({ row }: { row: Row<TirageInAuction> }) => {
      return <TextCell value={row.original.auction.ventesDescriptionPays} />;
    },
  },
];

const TirageInAuctionLinks = ({
  id,
  tirage,
  fields,
  type,
  auction,
}: ComplexLinkProps) => {
  const { t } = useTranslation();

  const { data: relatedData } = useGetComplexRelationFields(
    id,
    "tirageInAuction",
    type,
  );

  const [searchContent, setSearchContent] = useState("");

  const { mutate: deleteTirageInAuction } = useDeleteComplexLink(
    id,
    "tirageInAuction",
    type,
  );

  const [isAddMode, setIsAddMode] = useState(false);

  const handleDeleteRelation = useCallback(
    async (id: string) => {
      await deleteTirageInAuction({
        id,
        relation: "tirageInAuction",
      });
    },
    [deleteTirageInAuction],
  );

  const renderTirageInAuctionRowActions = useCallback(
    (data: TirageInAuction) => {
      const preview =
            type === "auction"
              ? {
                  previewType: "tirage" as const,
                  previewId: data.tirage.id,
                  previewElements: [
                    {
                      label: t("tirageDescriptionNumeroEnregistrement"),
                      value: data.tirage.tirageDescriptionNumeroEnregistrement ?? "",
                      type: "text" as const,
                    },
                    {
                      label: t("tirageDescriptionNotes"),
                      value: data.tirage.tirageDescriptionNotes ?? "",
                      type: "richtext" as const,
                    },
                    {
                      label: t("attachments"),
                      value: data.tirage.attachments ?? [],
                      type: "image" as const,
                    },
                  ],
                }
              : type === "tirage"
                ? {
                    previewType: "auction" as const,
                    previewId: data.auction.id,
                    previewElements: [
                      {
                        label: t("ventesDescriptionNumeroVente"),
                        value: data.auction.ventesDescriptionNumeroVente,
                        type: "text" as const,
                      },
                      {
                        label: t("ventesDescriptionResume"),
                        value: data.auction.ventesDescriptionResume,
                        type: "richtext" as const,
                      },
                    ],
                  }
                : null;

          if (!preview) return null;

          return (
            <LinkDrawerRowActions
              previewType={preview.previewType}
              previewId={preview.previewId}
              previewElements={preview.previewElements}
              onDelete={() => handleDeleteRelation(data.id)}
              editDrawer={({ open, onOpenChange }) => (
                <TirageInAuctionDrawer
                  key={data.id}
                  fields={fields}
                  data={data}
                  queryId={id}
                  type={type}
                  open={open}
                  onOpenChange={onOpenChange}
                />
              )}
            />
          );
    },
    [t, type, fields, handleDeleteRelation, id],
  );

  const columns: ColumnDef<TirageInAuction, any>[] = useMemo(() => {
    return [
      ...(type === "tirage"
        ? (columnsAuction.map((column) => ({
            ...column,
            header: typeof column.header === "string" ? t(column.header) : "",
          })) as ColumnDef<TirageInAuction, any>[])
        : type === "auction"
          ? [
              {
                accessorKey: "tirage.tirageDescriptionNumeroEnregistrement",
                header: t("tirageDescriptionNumeroEnregistrement"),
                enableResizing: true,
                cell: ({ row }: { row: Row<TirageInAuction> }) => (
                  <TextCell
                    value={
                      row.original.tirage?.tirageDescriptionNumeroEnregistrement ??
                      ""
                    }
                  />
                ),
              },
              {
                accessorKey: "tirageInAuctionVignette",
                header: "",
                size: 100,
                cell: ({ row }: { row: Row<TirageInAuction> }) => (
                  <ImageCell
                    value=""
                    attachments={row.original.tirage?.attachments ?? []}
                    domain="tirage"
                  />
                ),
              },
            ]
          : []),
      {
        accessorKey: "tirageDansVentesDescriptionNumeroLot",
        header: t("auctionLotNumber"),
      },
      ...(type === "auction"
        ? [
            {
              accessorKey: "tirageDansVentesDescriptionNotes",
              header: t("tirageDansVentesDescriptionNotes"),
              cell: ({ row }: { row: Row<TirageInAuction> }) => (
                <HtmlCell
                  value={
                    row.original.tirageDansVentesDescriptionNotes ?? ""
                  }
                />
              ),
            },
          ]
        : []),
      createActionsColumnDef<TirageInAuction>(({ row }) =>
        renderTirageInAuctionRowActions(row.original),
      ),
    ];
  }, [fields, handleDeleteRelation, id, t, type]);

  const datatableConf = useMemo(() => {
    if (type === "tirage") {
      return (
        <TirageToAuctionLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          tirage={tirage}
          isAddMode={isAddMode}
          columnsLink={columns}
          renderRowActions={renderTirageInAuctionRowActions}
          searchContent={searchContent}
          searchInput={
            <div className="flex items-center gap-2">
              <Input
                placeholder={t("search")}
                className="w-80"
                value={searchContent}
                onChange={(e) => setSearchContent(e.target.value)}
              />
            </div>
          }
          listGridTitle={t("auctionsRelated")}
          layoutBackLabel={t("relatedRecords")}
          setIsAddMode={setIsAddMode}
        />
      );
    } else {
      return (
        <AuctionToTirageLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          auction={auction}
          isAddMode={isAddMode}
          columnsLink={columns}
          renderRowActions={renderTirageInAuctionRowActions}
          searchContent={searchContent}
          searchInput={
            <div className="flex items-center gap-2">
              <Input
                placeholder={t("search")}
                className="w-80"
                value={searchContent}
                onChange={(e) => setSearchContent(e.target.value)}
              />
            </div>
          }
          listGridTitle={t("tiragesRelated")}
          layoutBackLabel={t("relatedRecords")}
          setIsAddMode={setIsAddMode}
        />
      );
    }
  }, [
    type,
    id,
    relatedData,
    fields,
    tirage,
    isAddMode,
    columns,
    searchContent,
    auction,
  ]);

  return <div className="w-full">{datatableConf}</div>;
};

type LinksTableProps<T> = {
  id: string;
  relatedData: T[];
  fields: FormLayoutElement[] | undefined;
  isAddMode: boolean;
  columnsLink: ColumnDef<T, any>[];
  renderRowActions?: (row: T) => ReactNode;
  searchContent: string;
  searchInput?: ReactNode;
  listGridTitle?: ReactNode;
  layoutBackLabel?: ReactNode;
  setIsAddMode?: Dispatch<SetStateAction<boolean>>;
};

type TirageToAuctionLinksProps = LinksTableProps<TirageInAuction> & {
  tirage: Tirage | undefined;
};

type AuctionToTirageLinksProps = LinksTableProps<TirageInAuction> & {
  auction: Auction | undefined;
};

const TirageToAuctionLinks = ({
  id,
  relatedData,
  fields,
  tirage,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: TirageToAuctionLinksProps) => {
  const { t } = useTranslation();
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  useEffect(() => {
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD = "tirage-links-auction-add";
  const STORAGE_KEY_LINKED = "tirage-links-auction-linked";
  const defaultVisibility = {
    ...publicAuctionColumnsVisibility,
    actions: true,
  };
  const [columnVisibilityAdd, setColumnVisibilityAdd] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    });
  const [columnVisibilityLinked, setColumnVisibilityLinked] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    });
  const [columnsOrderAdd, setColumnsOrderAdd] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinked, setColumnsOrderLinked] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_LINKED);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });

  const { data: notAlreadyRelatedAuctions } = useGetNotRelatedResource<Auction>(
    {
      resultType: "auction",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    },
  );

  const columnsAuctions: ColumnDef<Auction, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "ventesDescriptionNumeroVente",
        header: t("ventesDescriptionNumeroVente"),
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionNumeroVente} />;
        },
      },
      {
        accessorKey: "vignette",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<Auction> }) => {
          return (
            <ImageCell
              value={""}
              attachments={row.original.attachments ?? []}
              domain={"auction"}
            />
          );
        },
      },
      {
        accessorKey: "ventesDescriptionTitre",
        header: t("ventesDescriptionTitre"),
        size: 500,
        cell: (props) => {
          return <HtmlCell value={String(props.getValue() ?? "")} />;
        },
      },
      {
        accessorKey: "ventesDescriptionDate",
        header: t("ventesDescriptionDate"),
        size: 100,
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionDate} />;
        },
      },
      {
        accessorKey: "ventesDescriptionVille",
        header: t("ventesDescriptionVille"),
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionVille} />;
        },
      },
      {
        accessorKey: "ventesDescriptionPays",
        header: t("ventesDescriptionPays"),
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionPays} />;
        },
      },
      createActionsColumnDef<Auction>(({ row }) => {
          const data = row.original as Auction;
          if (!tirage) return null;
          return (
            <LinkDrawerRowActions
              previewType="auction"
              previewId={row.original.id}
              previewElements={[
                {
                  label: t("ventesDescriptionNumeroVente"),
                  value: row.original.ventesDescriptionNumeroVente,
                  type: "text",
                },
                {
                  label: t("ventesDescriptionResume"),
                  value: row.original.ventesDescriptionResume,
                  type: "richtext",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <TirageInAuctionDrawer
                  fields={fields}
                  key={row.original.id}
                  queryId={tirage.id}
                  type={"tirage"}
                  open={open}
                  onOpenChange={onOpenChange}
                  data={{
                    id: "",
                    auction: data,
                    tirage: tirage,
                    tirageDansVentesDescriptionNumeroLot: "",
                    tirageDansVentesDescriptionNotes: "",
                    tirageDansVentesDescriptionReproduitPage: "",
                    tirageDansVentesDescriptionEstimationHaute: "",
                    tirageDansVentesDescriptionEstimationBasse: "",
                    tirageDansVentesDescriptionVendu: false,
                    tirageDansVentesDescriptionResultatNonListe: false,
                    tirageDansVentesDescriptionNonVenu: false,
                    tirageDansVentesDescriptionNonVendu: false,
                    tirageDansVentesDescriptionNonReproduit: false,
                    tirageDansVentesDescriptionRetire: false,
                    tirageDansVentesDescriptionMiseAPrix: "",
                    tirageDansVentesDescriptionPrixMarteau: "",
                    tirageDansVentesDescriptionPrixAvecFrais: "",
                  }}
                />
              )}
            />
          );
        }),
    ];
  }, [tirage, fields, t]);

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: (relatedData ?? []).length,
  };
  const addModeTable = (
    <DataTable columns={columnsAuctions} data={notAlreadyRelatedAuctions?.data ?? []} pagination={{ pagination: getPagination, setPage: setPagination, totalSize: notAlreadyRelatedAuctions?.totalSize ?? 0 }} storageKey={STORAGE_KEY_ADD} options={{ enableColumnResizing: true, onColumnVisibilityChange: setColumnVisibilityAdd, onColumnOrderChange: setColumnsOrderAdd, state: { columnVisibility: columnVisibilityAdd, columnOrder: columnsOrderAdd } }} />
  );
  const listTable = (
    <DataTable columns={columnsLink} data={paginatedRelatedData} pagination={relatedPaginationProps} storageKey={STORAGE_KEY_LINKED} options={{ enableColumnResizing: true, onColumnVisibilityChange: setColumnVisibilityLinked, onColumnOrderChange: setColumnsOrderLinked, state: { columnVisibility: columnVisibilityLinked, columnOrder: columnsOrderLinked } }} />
  );
  if (listGridTitle && setIsAddMode && searchInput) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <AuctionLinkGridCard
                  item={row.auction}
                  t={t}
                  onTextClick={() => setSelectedRelationId(row.id)}
                />
                </GridCardWithActions>
                                <TirageInAuctionDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="tirage"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open) => {
                    if (!open) setSelectedRelationId(null);
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }
  return isAddMode ? addModeTable : listTable;
};

const AuctionToTirageLinks = ({
  id,
  relatedData,
  fields,
  auction,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: AuctionToTirageLinksProps) => {
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  useEffect(() => {
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD_T = "auction-links-tirage-add";
  const STORAGE_KEY_LINKED_T = "auction-links-tirage-linked";
  const defaultVisibilityT = { ...tiragesColumnsVisibility, actions: true };
  const [columnVisibilityAddT, setColumnVisibilityAddT] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD_T);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityT, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityT };
    });
  const [columnVisibilityLinkedT, setColumnVisibilityLinkedT] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED_T);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityT, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityT };
    });
  const [columnsOrderAddT, setColumnsOrderAddT] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD_T);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinkedT, setColumnsOrderLinkedT] = useState<string[]>(
    () => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED_T);
        if (saved) {
          const { order, columnOrder } = saved;
          const storedOrder = Array.isArray(order)
            ? order
            : Array.isArray(columnOrder)
              ? columnOrder
              : null;
          if (Array.isArray(storedOrder)) {
            const normalizedOrder = [...new Set(storedOrder)] as string[];
            return normalizedOrder.includes("actions")
              ? [
                  ...normalizedOrder.filter((id) => id !== "actions"),
                  "actions",
                ]
              : normalizedOrder;
          }
        }
      } catch {
        // ignore
      }
      return [];
    },
  );

  const { data: notAlreadyRelatedTirages } = useGetNotRelatedResource<Tirage>({
    resultType: "tirage",
    resourceId: id,
    pagination: getPagination,
    searchQuery: searchContent,
  });

  const { t } = useTranslation();
  const columnsTirages: ColumnDef<Tirage, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "tirageDescriptionNumeroEnregistrement",
        header: t("tirageDescriptionNumeroEnregistrement"),
        cell: ({ row }) => {
          return (
            <TextCell
              value={row.original.tirageDescriptionNumeroEnregistrement ?? ""}
            />
          );
        },
      },
      createActionsColumnDef<Tirage>(({ row }) => {
          const data = row.original as Tirage;
          if (!auction) return null;
          return (
            <LinkDrawerRowActions
              previewType="tirage"
              previewId={row.original.id}
              previewElements={[
                {
                  label: t("tirageDescriptionNumeroEnregistrement"),
                  value: data.tirageDescriptionNumeroEnregistrement ?? "",
                  type: "text",
                },
                {
                  label: t("tirageDescriptionNotes"),
                  value: data.tirageDescriptionNotes ?? "",
                  type: "richtext",
                },
                {
                  label: t("attachments"),
                  value: data.attachments ?? [],
                  type: "image",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <TirageInAuctionDrawer
                  fields={fields}
                  key={row.original.id}
                  queryId={auction.id}
                  type={"auction"}
                  open={open}
                  onOpenChange={onOpenChange}
                  data={{
                    id: "",
                    auction: auction,
                    tirage: data,
                    tirageDansVentesDescriptionNumeroLot: "",
                    tirageDansVentesDescriptionNotes: "",
                    tirageDansVentesDescriptionReproduitPage: "",
                    tirageDansVentesDescriptionEstimationHaute: "",
                    tirageDansVentesDescriptionEstimationBasse: "",
                    tirageDansVentesDescriptionVendu: false,
                    tirageDansVentesDescriptionResultatNonListe: false,
                    tirageDansVentesDescriptionNonVenu: false,
                    tirageDansVentesDescriptionNonVendu: false,
                    tirageDansVentesDescriptionNonReproduit: false,
                    tirageDansVentesDescriptionRetire: false,
                    tirageDansVentesDescriptionMiseAPrix: "",
                    tirageDansVentesDescriptionPrixMarteau: "",
                    tirageDansVentesDescriptionPrixAvecFrais: "",
                  }}
                />
              )}
            />
          );
        }),
    ];
  }, [auction, fields, t]);

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: (relatedData ?? []).length,
  };
  const addModeTable = (
    <DataTable columns={columnsTirages} data={notAlreadyRelatedTirages?.data ?? []} pagination={{ pagination: getPagination, setPage: setPagination, totalSize: notAlreadyRelatedTirages?.totalSize ?? 0 }} storageKey={STORAGE_KEY_ADD_T} options={{ enableColumnResizing: true, onColumnVisibilityChange: setColumnVisibilityAddT, onColumnOrderChange: setColumnsOrderAddT, state: { columnVisibility: columnVisibilityAddT, columnOrder: columnsOrderAddT } }} />
  );
  const listTable = (
    <DataTable columns={columnsLink} data={paginatedRelatedData} pagination={relatedPaginationProps} storageKey={STORAGE_KEY_LINKED_T} options={{ enableColumnResizing: true, onColumnVisibilityChange: setColumnVisibilityLinkedT, onColumnOrderChange: setColumnsOrderLinkedT, state: { columnVisibility: columnVisibilityLinkedT, columnOrder: columnsOrderLinkedT } }} />
  );
  if (listGridTitle && setIsAddMode && searchInput) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED_T}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <TirageLinkGridCard
                  tirage={row.tirage}
                  t={t}
                  onTextClick={() => setSelectedRelationId(row.id)}
                />
                </GridCardWithActions>
                                <TirageInAuctionDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="auction"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open) => {
                    if (!open) setSelectedRelationId(null);
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }
  return isAddMode ? addModeTable : listTable;
};
export default TirageInAuctionLinks;
